import { iconSetaDireitaBold } from '@bsds-lib-component/icons/icons';
import { BsdsIcon } from '@bsds-lib-component/react';
import React, { useId } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useBreadcrumb } from '../../context/BreadcrumbContext';
import { getRouteLabel, ROUTE_CONFIG } from '../../routes/routesConfig';

type BreadCrumbRenderableItem = {
  label: string;
  path?: string;
  onClick?: () => void;
};

export const BreadCrumb: React.FC = () => {
  const location = useLocation();
  const { suffixItems } = useBreadcrumb();
  const breadcrumbId = useId();

  const currentPath = location.pathname;
  const currentRoute = getRouteLabel(currentPath);

  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getRoutePath = (path: string): string => {
    const exactMatch = Object.values(ROUTE_CONFIG).find(config => config.path === path);
    if (exactMatch) return exactMatch.path;

    const partialMatch = Object.values(ROUTE_CONFIG).find(config =>
      path.startsWith(config.path) && config.path !== '/'
    );
    if (partialMatch) return partialMatch.path;

    return ROUTE_CONFIG.HOME.path;
  };

  const isHome =
    currentPath === ROUTE_CONFIG.HOME.path ||
    currentPath === ROUTE_CONFIG.HOME_ALT.path;

  const currentRoutePath = getRoutePath(currentPath);

  const breadcrumbItems: BreadCrumbRenderableItem[] = [
    { label: 'Portal', path: ROUTE_CONFIG.HOME.path },
    { label: capitalizeFirstLetter(ROUTE_CONFIG.HOME.label), path: ROUTE_CONFIG.HOME.path },
    ...(isHome
      ? []
      : [{ label: capitalizeFirstLetter(currentRoute), path: currentRoutePath }]),
    ...suffixItems,
  ];

  return (
    <nav
      className="bsds-display-flex bsds-align-items-center bsds-width-100 min-w-fit"
      role="navigation"
      aria-label="Navegação estrutural (breadcrumb)"
      id={breadcrumbId}
    >
      <ol
        className="bsds-display-flex bsds-align-items-center bsds-height-12 text-text-color"
        role="list"
        itemScope
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const isPortal = item.label === 'Portal';
          const nextItem = breadcrumbItems[index + 1];
          const isNextItemHome =
            nextItem?.label === capitalizeFirstLetter(ROUTE_CONFIG.HOME.label);
          const itemId = `${breadcrumbId}-item-${index}`;

          const shouldBeClickable = !isLast && !(isPortal && isNextItemHome) && (Boolean(item.path) || Boolean(item.onClick));

          return (
            <li
              key={`${item.label}-${index}`}
              role="listitem"
              itemScope
              itemProp="itemListElement"
              aria-current={isLast ? 'page' : undefined}
            >
              {shouldBeClickable ? (
                item.onClick ? (
                  <button
                    type="button"
                    onClick={item.onClick}
                    style={{ fontSize: '14px' }}
                    className="breadcrumb-link text-neutral-500 hover:text-primary-600 transition-colors duration-200 bg-transparent border-0 p-0 cursor-pointer"
                    aria-label={`Navegar para ${item.label}`}
                    id={itemId}
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    to={item.path!}
                    style={{ fontSize: '14px' }}
                    className="breadcrumb-link text-neutral-500 hover:text-primary-600 transition-colors duration-200"
                    itemProp="item"
                    aria-label={`Navegar para ${item.label}`}
                    id={itemId}
                  >
                    {item.label}
                  </Link>
                )
              ) : (
                <span
                  className={`text-[14px] ${isLast ? 'font-semibold text-neutral-500' : 'font-normal'}`}
                  itemProp="name"
                  aria-label={isLast ? `${item.label} (página atual)` : item.label}
                  id={itemId}
                >
                  {item.label}
                </span>
              )}

              {index < breadcrumbItems.length - 1 && (
                <BsdsIcon
                  icon={iconSetaDireitaBold}
                  className="bsds-mh-8 w-2.5 h-2.5 text-primary-base"
                  aria-hidden="true"
                  aria-label="Separador"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default BreadCrumb;
